import React, { useEffect, useState } from 'react';
import {
  Box, Drawer, Typography, Chip, Button, Divider, Table, TableBody,
  TableRow, TableCell,
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CircularProgress from '@mui/material/CircularProgress';
import api from '../../../../services/api';
import { useTopologyBase } from './topologyApi';

/**
 * Detalhe da linha clicada.
 *
 * A versão anterior reimprimia as mesmas colunas que já estavam visíveis na
 * linha — abrir não dizia nada de novo. Aqui o drawer mostra o que a tabela
 * NÃO mostra: o equipamento visto por TODAS as abas de uma vez. Clicar numa
 * linha de Segurança traz junto as sessões BGP dele, as VLANs, as rotas e o
 * MPLS, que é a pergunta que vem depois de "esse aqui está com telnet aberto".
 *
 * As linhas são buscadas por aba no momento em que o drawer abre, filtradas
 * pelo host no servidor. Antes vinham de graça porque a tela carregava todas
 * as abas inteiras — o que era justamente o problema (2,28 MB por empresa).
 * Uma requisição por aba com CAP linhas cada é muito mais barato que isso.
 *
 * Na aba de VLANs a entidade não é um equipamento e sim o VID, então o
 * conteúdo é outro — quem usa e quem roteia.
 */

const CAP = 12;   // linhas por seção antes de colapsar em "+N"

function Secao({ titulo, cols, linhas, ocultarCol, total }) {
  if (!linhas.length) return null;
  const mostra = linhas.slice(0, CAP);
  const quantas = total != null ? total : linhas.length;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: 0.5, color: '#64748b', mb: 0.5 }}
      >
        {titulo}
        <Typography component="span" sx={{ fontSize: 11, color: '#94a3b8', ml: 0.5 }}>
          ({quantas})
        </Typography>
      </Typography>
      <Table size="small" sx={{ '& td': { border: 0, py: 0.25, px: 0.5, fontSize: 12 } }}>
        <TableBody>
          {mostra.map((row, i) => (
            <TableRow key={i}>
              {row.map((c, ci) => (
                ci === ocultarCol ? null : (
                  <TableCell
                    key={ci}
                    sx={{
                      color: c === '' || c === '—' || c == null ? '#cbd5e1' : '#0f172a',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      fontWeight: ci === (ocultarCol === 0 ? 1 : 0) ? 600 : 400,
                    }}
                  >
                    {c === '' || c == null ? '—' : String(c)}
                  </TableCell>
                )
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {quantas > mostra.length && (
        <Typography sx={{ fontSize: 11, color: '#94a3b8', px: 0.5 }}>
          +{quantas - mostra.length} não listadas — use a seleção de host pra ver todas
        </Typography>
      )}
      <Typography sx={{ fontSize: 10, color: '#cbd5e1', px: 0.5 }}>
        {cols.filter((_, i) => i !== ocultarCol).join(' · ')}
      </Typography>
    </Box>
  );
}

export default function HostDossier({ aberto, onFechar, host, vlan, tabs, onFiltrarHost }) {
  const base = useTopologyBase();
  const alvo = host || vlan;
  const [porAba, setPorAba] = useState({});
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    let vivo = true;
    if (!aberto || !base) { setPorAba({}); return undefined; }
    if (vlan) {
      const f = JSON.stringify([{ col: 0, op: 'eq', value: vlan }]);
      api.get(`${base}/tables/vlans?limite=1&filtros=${encodeURIComponent(f)}`)
        .then((res) => vivo && setPorAba({ vlans: { linha: (res.data?.rows || [])[0] } }))
        .catch(() => vivo && setPorAba({}));
      return () => { vivo = false; };
    }
    if (!host) { setPorAba({}); return undefined; }
    setCarregando(true);
    const alvos = tabs.filter((t) => t.host_col != null);
    Promise.all(alvos.map((t) => {
      const f = JSON.stringify([{
        col: t.host_col,
        op: t.host_col === 0 ? 'eq' : 'contains',
        value: host,
      }]);
      return api
        .get(`${base}/tables/${t.key}?limite=${CAP}&filtros=${encodeURIComponent(f)}`)
        .then((res) => [t.key, { rows: res.data?.rows || [], total: res.data?.total || 0 }])
        .catch(() => [t.key, { rows: [], total: 0 }]);
    })).then((pares) => {
      if (!vivo) return;
      setPorAba(Object.fromEntries(pares));
    }).finally(() => vivo && setCarregando(false));
    return () => { vivo = false; };
  }, [aberto, host, vlan, base, tabs]);

  return (
    <Drawer anchor="right" open={!!aberto} onClose={onFechar}>
      <Box sx={{ width: 460, p: 2, bgcolor: '#ffffff', height: '100%', overflowY: 'auto' }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#0f172a', wordBreak: 'break-all' }}>
          {alvo || '—'}
        </Typography>
        {host && (
          <Button
            size="small"
            startIcon={<FilterAltIcon fontSize="small" />}
            onClick={() => { onFiltrarHost(host); onFechar(); }}
            sx={{ mt: 0.5, mb: 1 }}
          >
            filtrar todas as guias por este host
          </Button>
        )}
        <Divider sx={{ mb: 2 }} />

        {host && carregando && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        )}
        {host && !carregando && tabs.map((t) => {
          if (t.host_col == null) return null;
          const d = porAba[t.key];
          if (!d || !d.rows.length) return null;
          return (
            <Secao
              key={t.key}
              titulo={t.title}
              cols={t.cols}
              linhas={d.rows}
              total={d.total}
              ocultarCol={t.host_col === 0 ? 0 : null}
            />
          );
        })}
        {host && !carregando && !Object.values(porAba).some((d) => d.rows.length) && (
          <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
            Nenhum registro para este equipamento nas outras abas.
          </Typography>
        )}

        {vlan && (() => {
          const row = (porAba.vlans || {}).linha;
          if (!row) return null;
          const devices = String(row[4] || '').split(',').map((x) => x.trim()).filter(Boolean);
          const gws = String(row[3] || '').split(',').map((x) => x.trim()).filter((x) => x && x !== '—');
          return (
            <>
              <Secao
                titulo="gateways (SVI)"
                cols={['device']}
                linhas={gws.length ? gws.map((g) => [g]) : []}
                ocultarCol={null}
              />
              {!gws.length && (
                <Typography sx={{ fontSize: 12, color: '#b45309', mb: 2 }}>
                  Nenhum SVI encontrado — o gateway está fora do parque coletado,
                  ou esta VLAN é transporte puro, ou sobrou de um serviço desativado.
                </Typography>
              )}
              <Typography
                sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: 0.5, color: '#64748b', mb: 0.5 }}
              >
                devices ({devices.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {devices.map((d) => (
                  <Chip
                    key={d}
                    size="small"
                    label={d}
                    onClick={() => { onFiltrarHost(d); onFechar(); }}
                    sx={{ bgcolor: '#e2e8f0', color: '#0f172a', cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </>
          );
        })()}
      </Box>
    </Drawer>
  );
}
